import { MeterDeviceType } from '../advanced-settings/powermeter'
import {
  ChargingSchedule,
  EebusChargingStation,
  Emc,
  Htc320hIo,
  InstallationFormModel,
  ModbusTcpChargingStation,
  MyPvIo,
  Storage,
  StorageIo,
} from '../installation/forms'
import { getPowerConsumptionLimit, getTimeAsISO, getTreatHtcAsNonSgReady } from '../installation/utils'
import { getUuid } from '../utils'

export const getDefaultEmc = (name: string): Emc => {
  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'EMC',
    name,
    pairingId: '',
    initialPairingId: '',
    deviceStatus: 'unknown',
    deviceStatusCodeBits: null,
    isPaired: false,
    serialNumber: '',
    emcHardwareDeviceId: null,
  }
}

export const getDefaultStorage = (name: string): Storage => {
  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'Storage',
    name,
    pairingId: '',
    initialPairingId: '',
    deviceStatus: 'unknown',
    deviceStatusCodeBits: null,
    isPaired: false,
    serialNumber: '',
    deviceType: null,
    storageHardwareDeviceId: null,
  }
}

export const getDefaultModbusTcp = (
  name: string,
  isEmcSinglePhase: boolean,
  brand: ModbusTcpChargingStation['brand'],
  reference: ModbusTcpChargingStation['reference']
): ModbusTcpChargingStation => {
  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'ModbusTCP Evse',
    name,
    brand,
    reference,
    deviceReference: '',
    pairingId: '',
    deviceStatus: 'unknown',
    deviceStatusCodeBits: null,
    isPaired: false,
    serialNumber: '',

    protection: 16,
    fallbackMaxCurrent: 16,
    phaseManagement: isEmcSinglePhase ? '1PhBlocked' : 'AutoPhaseSwitch',
    phaseMapping: '123',
    minCharge: 6,

    carAtHomeBehavior: {
      isEnabled: false,
      chargingPointOperatorWs: 'wss://',
      ocppId: '',
      isCredentialsActive: false,
      password: '',
    },

    isCableLocked: false,

    ledIntensityPercentage: 100,
    midMeter: 'no mid meter',
    initialAccessManagementOptions: 'freeAccess',
    accessManagementOptions: 'freeAccess',
    chargingMode: 'pvImmediate',
    holdingTime: 1,
    solarRatio: 100,
    safePvSchedule: getDefaultModbusTcpSafePvSchedule(),
    efficientPvSchedule: getDefaultModbusTcpEfficientPvSchedule(),
    initialPairingId: '',
    rawData: null,
  }
}

export const getDefaultModbusTcpSafePvSchedule = (): ChargingSchedule => {
  return [
    {
      departureTime: getTimeAsISO('00:00'),
      energyWanted: 7,
      weekDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true,
      },
    },
  ]
}

export const getDefaultModbusTcpEfficientPvSchedule = (): ChargingSchedule => {
  return [
    {
      departureTime: getTimeAsISO('06:00'),
      energyWanted: 7,
      weekDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true,
      },
    },
  ]
}

export const getDefaultEebus = (
  name: string,
  brand: EebusChargingStation['brand'],
  reference: EebusChargingStation['reference']
): EebusChargingStation => {
  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'EEBus Evse',
    name,
    brand,
    reference,
    pairingId: '',
    deviceStatus: 'unknown',
    isPaired: false,
    serialNumber: '',

    protection: 16,
    holdingTime: 1,
    solarRatio: 100,
    minCharge: 6,
    chargingMode: 'standard',
    initialPairingId: '',
    rawData: null,
    safePvSchedule: getDefaultEebusSafePvSchedule(),
    efficientPvSchedule: getDefaultEebusEfficientPvSchedule(),
    deviceStatusCodeBits: null,
  }
}

export const getDefaultEebusSafePvSchedule = (): ChargingSchedule => {
  return [
    {
      departureTime: getTimeAsISO('00:00'),
      energyWanted: 7,
      weekDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true,
      },
    },
  ]
}

export const getDefaultEebusEfficientPvSchedule = (): ChargingSchedule => {
  return [
    {
      departureTime: getTimeAsISO('06:00'),
      energyWanted: 7,
      weekDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true,
      },
    },
  ]
}

export const getDefaultHeatpumpIO = (name: string): InstallationFormModel['heatpump'] => {
  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'Heatpump',
    name: name,
    pairingId: '',
    deviceStatus: 'unknown',
    deviceStatusCodeBits: null,
    isPaired: false,
    serialNumber: '',

    nominalPower: 2000,
    initialPairingId: '',
  }
}

export const getDefaultMeter = (
  name: string,
  deviceType: MeterDeviceType,
  peakPower: number,
  /** pairing id */
  address: string
): InstallationFormModel['meters'][number] => {
  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'Meter',
    name: name,
    deviceType,
    pairingId: address,
    deviceStatus: 'unknown',
    deviceStatusCodeBits: null,
    isPaired: false,
    serialNumber: '',
    wiringMode: 'Normal',

    peakPower: deviceType === 'PVExtern' ? peakPower : null,
  }
}

export const getDefaultHtc320hIoInterface = (name: string, address: string, installation: InstallationFormModel): Htc320hIo => {
  const { generalSettings, heatpump, ioInterfaces } = installation
  const treatHtcAsNonSgReady = getTreatHtcAsNonSgReady(generalSettings, ioInterfaces.storage, heatpump)
  const nominalPower = treatHtcAsNonSgReady ? 0 : 2000

  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'HTC320H I/O',
    name,
    pairingId: address,
    deviceStatus: 'unknown',
    deviceStatusCodeBits: null,
    isPaired: false,
    serialNumber: '',

    nominalPower,
    isConsumptionActive: false,

    totalPowerConsumption: getPowerConsumptionLimit(installation, nominalPower),

    isProductionActive: false,

    rawData: null,
  }
}

export const getDefaultStorageIoInterface = (name: string, storageDeviceLinkId: string): StorageIo => {
  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'Storage I/O',
    name,
    pairingId: '',
    deviceStatus: 'unknown',
    deviceStatusCodeBits: null,
    isPaired: false,
    serialNumber: '',

    nominalPower: 2000,
    deviceLinkId: -1,
    storageDeviceLinkId: Number(storageDeviceLinkId),
  }
}

export const getDefaultMyPvIoInterface = (name: string): MyPvIo => {
  return {
    id: getUuid(),
    isNew: true,
    deviceId: '',
    navigationId: getUuid(),
    deviceCategory: 'MyPV I/O',
    name,
    pairingId: '',
    deviceStatus: 'unknown',
    deviceStatusCodeBits: null,
    isPaired: false,
    serialNumber: '',

    optimizationMode: 'All',
    boostConfig: [],
    rawData: null,
  }
}
